import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { HoloCard } from '@/components/CyberComponents';
import { useRealAlerts } from '@/components/RandomAlerts';
import { api } from '@/App';
import { 
  AlertTriangle, Flame, Droplets, Thermometer, Zap, Cat, Bug, WifiOff, 
  Eye, Video, Bell, Radio, Siren, BellRing, AlertCircle, Clock, Trash2,
  Link, ExternalLink, CheckCircle, RefreshCw, Wifi, WifiHigh
} from 'lucide-react';
import { triggerHALEye } from '@/components/HALEyeFullscreen';
import { useHALVideo } from '@/components/HALVideo';
import audioManager from '@/utils/AudioManager';

const Admin = () => {
  const { createRealAlert } = useRealAlerts();
  const { triggerVideo } = useHALVideo();
  const [alertLog, setAlertLog] = useState([]);
  const [hcStatus, setHcStatus] = useState(null);
  const [hcCode, setHcCode] = useState('');
  const [hcLoading, setHcLoading] = useState(false);
  
  // Tuya Discovery state
  const [tuyaDiscovering, setTuyaDiscovering] = useState(false);
  const [tuyaResult, setTuyaResult] = useState(null);

  // Load Home Connect status
  useEffect(() => {
    api.get('/integrations/homeconnect/status').then(r => setHcStatus(r.data)).catch(() => {});
  }, []);

  // Tuya auto-discover function
  const discoverTuya = async () => {
    setTuyaDiscovering(true);
    setTuyaResult(null);
    try {
      const res = await api.post('/integrations/tuya/sync');
      setTuyaResult(res.data);
      addToLog('TUYA SYNC', 'SYSTÈME', '#00ff9f');
    } catch (e) {
      setTuyaResult({ success: false, error: e.message || 'Erreur de synchronisation' });
      addToLog('TUYA ERR', 'SYSTÈME', '#ff0000');
    }
    setTuyaDiscovering(false);
  };

  const submitHcCode = async () => {
    if (!hcCode.trim()) return;
    setHcLoading(true);
    try {
      const res = await api.post('/integrations/homeconnect/auth', { code: hcCode.trim() });
      if (res.data?.success) {
        setHcStatus(prev => ({ ...prev, authenticated: true }));
        setHcCode('');
      }
    } catch (e) { console.error(e); }
    setHcLoading(false);
  };

  // Charger le log depuis localStorage au démarrage
  useEffect(() => {
    const saved = localStorage.getItem('alertLog');
    if (saved) {
      try {
        setAlertLog(JSON.parse(saved));
      } catch (e) {
        setAlertLog([]);
      }
    }
  }, []);

  // Sauvegarder le log à chaque changement
  useEffect(() => {
    localStorage.setItem('alertLog', JSON.stringify(alertLog.slice(-50))); // Garder les 50 dernières
  }, [alertLog]);

  // === DIAGNOSTIC SYSTÈME ===
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState(null);

  const runDiagnostic = async () => {
    setDiagLoading(true);
    setDiagError(null);
    try {
      const res = await api.get('/system/full-diagnostic');
      setDiagnostic(res.data);
      addToLog('DIAGNOSTIC', 'SYSTÈME', '#00f0ff');
    } catch (e) {
      setDiagError(e.message || 'Erreur diagnostic');
      addToLog('DIAG ERR', 'SYSTÈME', '#ff0000');
    }
    setDiagLoading(false);
  };

  const runAutoRepair = async () => {
    setDiagLoading(true);
    try {
      const res = await api.post('/system/auto-repair');
      if (res.data?.repairs_successful?.length > 0) {
        addToLog('RÉPARÉ', 'SYSTÈME', '#00ff9f');
      }
      // Relancer le diagnostic après réparation
      await runDiagnostic();
    } catch (e) {
      setDiagError(e.message || 'Erreur auto-repair');
    }
    setDiagLoading(false);
  };

  const getStatusColor = (status) => {
    if (status === 'healthy') return 'text-green-400';
    if (status === 'warning') return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy') return '✓';
    if (status === 'warning') return '⚠';
    return '✗';
  };

  const addToLog = (type, category, color) => {
    const now = new Date();
    const entry = {
      id: Date.now(),
      date: now.toLocaleDateString('fr-FR'),
      time: now.toLocaleTimeString('fr-FR'),
      type,
      category,
      color
    };
    setAlertLog(prev => [entry, ...prev].slice(0, 50));
  };

  const clearLog = () => {
    setAlertLog([]);
    localStorage.removeItem('alertLog');
  };

  const testRealAlert = (type, data, label, color) => {
    createRealAlert(type, data);
    addToLog(label, 'RÉELLE', color);
  };

  const triggerLudicAlert = (type, label, color) => {
    window.dispatchEvent(new CustomEvent('trigger-ludic-alert', { detail: type }));
    addToLog(label, 'LUDIQUE', color);
  };

  const testSounds = [
    { name: 'ALERT', icon: Siren, action: () => audioManager.playAlertSequence(), color: '#ff0000' },
    { name: 'BEEP', icon: Radio, action: () => audioManager.playBeep(), color: '#00ff9f' },
    { name: 'ERR', icon: AlertCircle, action: () => audioManager.playError(), color: '#ff00ff' },
    { name: 'OK', icon: Bell, action: () => audioManager.playSuccess(), color: '#00f0ff' },
    { name: 'NOTIF', icon: BellRing, action: () => audioManager.playNotification(), color: '#ffff00' },
  ];

  const realAlertsTests = [
    { type: 'smoke', icon: Flame, label: 'FUMÉE', color: '#ff0000', data: { room: 'Cuisine' } },
    { type: 'water_leak', icon: Droplets, label: 'FUITE', color: '#00f0ff', data: { room: 'SDB' } },
    { type: 'temperature', icon: Thermometer, label: 'TEMP', color: '#ffff00', data: { room: 'Chambre', value: 38, threshold: 30 } },
    { type: 'intrusion', icon: AlertTriangle, label: 'INTRUSION', color: '#ff00ff', data: { room: 'Garage' } },
    { type: 'electrical_overload', icon: Zap, label: 'SURCHARGE', color: '#ffff00', data: { current: 11.5, subscription: 10, tripThreshold: 13 } },
  ];

  const ludicAlertsInfo = [
    { type: 'leak', icon: Droplets, label: 'ROBINET', color: '#00f0ff' },
    { type: 'fire', icon: Flame, label: 'FEU', color: '#ff0000' },
    { type: 'cat', icon: Cat, label: 'CHAT', color: '#ff00ff' },
    { type: 'wildlife', icon: Bug, label: 'SANGLIER', color: '#00ff9f' },
    { type: 'squirrel', icon: Bug, label: 'ÉCUREUIL', color: '#ffaa00' },
    { type: 'electrical', icon: Zap, label: 'ÉLEC', color: '#ffff00' },
    { type: 'disconnection', icon: WifiOff, label: 'WIFI', color: '#ff00ff' },
    { type: 'hal', icon: Eye, label: 'HAL 9000', color: '#ff0000' },
  ];

  return (
    <Layout title="ADMIN_TEST">
      <div className="h-full grid grid-cols-12 gap-2">
        
        {/* Colonne gauche: Contrôles (8 colonnes) */}
        <div className="col-span-8 grid grid-rows-6 gap-2">
          
          {/* Row 1: HAL + Sons + Status */}
          <div className="row-span-2 grid grid-cols-3 gap-2">
            {/* HAL 9000 */}
            <div className="bg-black/50 border border-red-500/50 rounded p-2 flex flex-col">
              <div className="text-[10px] text-red-400 font-mono mb-1">HAL 9000</div>
              <div className="flex-1 grid grid-cols-2 gap-1">
                <button
                  onClick={() => { triggerHALEye(); addToLog('HAL EYE', 'HAL', '#ff0000'); }}
                  className="bg-black border border-red-600 rounded p-1 hover:bg-red-900/30 flex flex-col items-center justify-center"
                  data-testid="trigger-hal-eye"
                >
                  <img src="/hal-eye.gif" alt="HAL" className="w-8 h-8" />
                  <span className="text-[8px] text-red-400 font-mono">EYE</span>
                </button>
                <button
                  onClick={() => { triggerVideo('TEST'); addToLog('HAL VIDEO', 'HAL', '#ff0000'); }}
                  className="bg-black border border-red-600 rounded p-1 hover:bg-red-900/30 flex flex-col items-center justify-center"
                  data-testid="trigger-hal-video"
                >
                  <Video className="w-8 h-8 text-red-500" />
                  <span className="text-[8px] text-red-400 font-mono">VIDEO</span>
                </button>
              </div>
            </div>

            {/* Sons */}
            <div className="bg-black/50 border border-purple-500/50 rounded p-2">
              <div className="text-[10px] text-purple-400 font-mono mb-1">SONS</div>
              <div className="grid grid-cols-5 gap-1 h-[calc(100%-20px)]">
                {testSounds.map((sound) => {
                  const Icon = sound.icon;
                  return (
                    <button
                      key={sound.name}
                      onClick={() => { sound.action(); addToLog(sound.name, 'SON', sound.color); }}
                      className="bg-black border rounded p-1 hover:bg-white/5 flex flex-col items-center justify-center"
                      style={{ borderColor: `${sound.color}80` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: sound.color }} />
                      <span className="text-[6px] font-mono mt-0.5" style={{ color: sound.color }}>{sound.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status */}
            <div className="bg-black/50 border border-cyan-500/50 rounded p-2 flex flex-col justify-between">
              <div>
                <div className="text-[10px] text-cyan-400 font-mono">STATUT</div>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[9px] text-green-400 font-mono">OPÉRATIONNEL</span>
                </div>
              </div>
              <div className="text-[8px] text-gray-500 font-mono">HAL 3000 v3.0</div>
            </div>
          </div>

          {/* Row 2-3: Alertes Réelles */}
          <HoloCard className="row-span-2 p-2">
            <div className="text-[10px] text-cyan-400 font-mono mb-1 flex items-center">
              <AlertTriangle className="w-3 h-3 mr-1" />
              ALERTES RÉELLES (attente action humaine)
            </div>
            <div className="grid grid-cols-5 gap-2 h-[calc(100%-20px)]">
              {realAlertsTests.map((alert) => {
                const Icon = alert.icon;
                return (
                  <button
                    key={alert.type}
                    onClick={() => testRealAlert(alert.type, alert.data, alert.label, alert.color)}
                    className="bg-black border-2 rounded p-2 hover:bg-white/5 flex flex-col items-center justify-center"
                    style={{ borderColor: alert.color, boxShadow: `0 0 10px ${alert.color}30` }}
                    data-testid={`test-alert-${alert.type}`}
                  >
                    <Icon className="w-8 h-8" style={{ color: alert.color, filter: `drop-shadow(0 0 5px ${alert.color})` }} />
                    <span className="text-[9px] font-mono font-bold mt-1" style={{ color: alert.color }}>{alert.label}</span>
                  </button>
                );
              })}
            </div>
          </HoloCard>

          {/* Row 4-5: Alertes Ludiques */}
          <HoloCard className="row-span-2 p-2">
            <div className="text-[10px] text-yellow-400 font-mono mb-1 flex items-center">
              <Bell className="w-3 h-3 mr-1" />
              ALERTES LUDIQUES (fausses alertes + robots SF)
            </div>
            <div className="grid grid-cols-4 gap-2 h-[calc(100%-20px)]">
              {ludicAlertsInfo.map((alert) => {
                const Icon = alert.icon;
                return (
                  <button
                    key={alert.type}
                    onClick={() => triggerLudicAlert(alert.type, alert.label, alert.color)}
                    className="bg-black border-2 rounded p-2 hover:bg-white/5 flex flex-col items-center justify-center"
                    style={{ borderColor: `${alert.color}80`, boxShadow: `0 0 8px ${alert.color}20` }}
                    data-testid={`test-ludic-${alert.type}`}
                  >
                    <Icon className="w-8 h-8" style={{ color: alert.color }} />
                    <span className="text-[9px] font-mono font-bold mt-1" style={{ color: alert.color }}>{alert.label}</span>
                  </button>
                );
              })}
            </div>
          </HoloCard>
        </div>

        {/* Colonne droite: Intégrations + Log (4 colonnes) */}
        <div className="col-span-4 flex flex-col gap-2 overflow-hidden">
          
          {/* Home Connect Auth */}
          <div className="bg-black/70 border border-blue-500/50 rounded p-2 flex-shrink-0" style={{ boxShadow: '0 0 10px rgba(0,100,255,0.1)' }}>
            <div className="text-[10px] text-blue-400 font-mono mb-1.5 flex items-center gap-1">
              <Link className="w-3 h-3" />
              HOME CONNECT (BOSCH)
            </div>
            {hcStatus?.authenticated ? (
              <div className="flex items-center gap-1.5 text-green-400">
                <CheckCircle className="w-3.5 h-3.5" />
                <span className="text-[10px] font-mono font-bold">CONNECTÉ - Four, Lave-vaisselle</span>
              </div>
            ) : hcStatus?.configured ? (
              <div className="space-y-1.5">
                <div className="text-[9px] text-gray-400 font-mono">
                  Connectez vos appareils Bosch en un clic :
                </div>
                <a 
                  href={hcStatus.auth_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500/20 border border-blue-500 rounded text-sm text-blue-400 font-mono font-bold hover:bg-blue-500/30 transition-all"
                  data-testid="hc-auth-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  CONNECTER HOME CONNECT
                </a>
                <div className="text-[8px] text-gray-600 font-mono">
                  Redirect URI à enregistrer dans le portail développeur :
                </div>
                <div className="text-[8px] text-cyan-400/70 font-mono bg-black/50 px-1.5 py-0.5 rounded border border-gray-800 break-all select-all">
                  {hcStatus.redirect_uri}
                </div>
                <div className="text-[8px] text-gray-500 font-mono mt-1">
                  Si redirigé vers une page d'erreur, collez l'URL complète ici :
                </div>
                <div className="flex gap-1">
                  <input
                    value={hcCode}
                    onChange={e => {
                      const val = e.target.value;
                      // Auto-extract code from URL if pasted
                      const match = val.match(/[?&]code=([^&]+)/);
                      setHcCode(match ? match[1] : val);
                    }}
                    placeholder="URL ou code..."
                    className="flex-1 bg-black border border-blue-500/30 rounded px-2 py-1 text-[10px] font-mono text-blue-400 placeholder-gray-600"
                    data-testid="hc-code-input"
                  />
                  <button
                    onClick={submitHcCode}
                    disabled={hcLoading || !hcCode.trim()}
                    className="px-2 py-1 bg-blue-500/20 border border-blue-500 rounded text-[9px] font-mono text-blue-400 hover:bg-blue-500/30 disabled:opacity-30"
                    data-testid="hc-code-submit"
                  >
                    {hcLoading ? '...' : 'OK'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-[9px] text-gray-500 font-mono">Client ID/Secret non configurés</div>
            )}
          </div>

          {/* Tuya/SmartLife Auto-Discover */}
          <div className="bg-black/70 border border-green-500/50 rounded p-2 flex-shrink-0" style={{ boxShadow: '0 0 10px rgba(0,255,100,0.1)' }}>
            <div className="text-[10px] text-green-400 font-mono mb-1.5 flex items-center gap-1">
              <WifiHigh className="w-3 h-3" />
              TUYA / SMARTLIFE
            </div>
            
            <button
              onClick={discoverTuya}
              disabled={tuyaDiscovering}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500 rounded text-sm text-green-400 font-mono font-bold hover:bg-green-500/30 transition-all disabled:opacity-50"
              data-testid="tuya-discover-btn"
            >
              <RefreshCw className={`w-4 h-4 ${tuyaDiscovering ? 'animate-spin' : ''}`} />
              {tuyaDiscovering ? 'SYNCHRONISATION...' : 'AUTO-DÉCOUVERTE'}
            </button>
            
            {tuyaResult && (
              <div className={`mt-2 p-2 rounded border ${tuyaResult.success ? 'bg-green-900/20 border-green-500/50' : 'bg-red-900/20 border-red-500/50'}`}>
                {tuyaResult.success ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      <span className="text-[10px] font-mono font-bold">SYNCHRONISÉ</span>
                    </div>
                    <div className="text-[9px] font-mono text-gray-300">
                      <div>Total: <span className="text-green-400">{tuyaResult.total_devices}</span> appareils</div>
                      <div>En ligne: <span className="text-green-400">{tuyaResult.online}</span> | Hors ligne: <span className="text-red-400">{tuyaResult.offline}</span></div>
                      {tuyaResult.new_devices > 0 && (
                        <div className="text-cyan-400">+ {tuyaResult.new_devices} nouveaux appareils détectés !</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-red-400 space-y-1">
                    <div className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 flex-shrink-0" />
                      <span className="text-[9px] font-mono">
                        {tuyaResult.error && /quota|trial|not connected|clientid/i.test(tuyaResult.error)
                          ? 'Quota Tuya Trial épuisé (extension en attente)'
                          : tuyaResult.error}
                      </span>
                    </div>
                    {tuyaResult.error && /quota|trial|not connected|clientid/i.test(tuyaResult.error) && (
                      <a
                        href="/settings/tuya-local-keys"
                        className="block text-[9px] text-amber-300 hover:text-amber-200 underline font-mono"
                        data-testid="tuya-fallback-local-link"
                      >
                        → Utiliser la saisie manuelle des local_keys
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className="text-[8px] text-gray-500 font-mono mt-1.5">
              Synchronise tous les appareils SmartLife/Tuya
            </div>
          </div>

          {/* DIAGNOSTIC SYSTÈME */}
          <div className="bg-black/70 border border-orange-500/50 rounded p-2 flex-shrink-0" style={{ boxShadow: '0 0 10px rgba(255,165,0,0.1)' }}>
            <div className="text-[10px] text-orange-400 font-mono mb-1.5 flex items-center gap-1">
              <Wifi className="w-3 h-3" />
              DIAGNOSTIC SYSTÈME
            </div>
            
            <div className="flex gap-1 mb-2">
              <button
                onClick={runDiagnostic}
                disabled={diagLoading}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-orange-500/20 border border-orange-500 rounded text-[10px] text-orange-400 font-mono font-bold hover:bg-orange-500/30 transition-all disabled:opacity-50"
                data-testid="run-diagnostic-btn"
              >
                <RefreshCw className={`w-3 h-3 ${diagLoading ? 'animate-spin' : ''}`} />
                {diagLoading ? 'SCAN...' : 'SCANNER'}
              </button>
              {diagnostic?.repair_suggestions?.length > 0 && (
                <button
                  onClick={runAutoRepair}
                  disabled={diagLoading}
                  className="px-2 py-1.5 bg-cyan-500/20 border border-cyan-500 rounded text-[10px] text-cyan-400 font-mono font-bold hover:bg-cyan-500/30 transition-all disabled:opacity-50"
                  data-testid="auto-repair-btn"
                >
                  RÉPARER
                </button>
              )}
            </div>
            
            {diagError && (
              <div className="p-1.5 bg-red-900/20 border border-red-500/50 rounded mb-2">
                <span className="text-[9px] text-red-400 font-mono">{diagError}</span>
              </div>
            )}
            
            {diagnostic && (
              <div className="space-y-1 max-h-[120px] overflow-y-auto custom-scrollbar">
                {/* Summary Bar */}
                <div className="flex gap-1 text-[8px] font-mono mb-1">
                  <span className="text-green-400">✓ {diagnostic.summary?.healthy || 0}</span>
                  <span className="text-yellow-400">⚠ {diagnostic.summary?.warning || 0}</span>
                  <span className="text-red-400">✗ {diagnostic.summary?.critical || 0}</span>
                </div>
                
                {/* Service Status Grid */}
                <div className="grid grid-cols-3 gap-1">
                  {Object.entries(diagnostic.services || {}).map(([name, svc]) => (
                    <div 
                      key={name}
                      className={`p-1 rounded border text-center ${
                        svc.status === 'healthy' 
                          ? 'bg-green-900/20 border-green-500/30' 
                          : svc.status === 'warning'
                          ? 'bg-yellow-900/20 border-yellow-500/30'
                          : 'bg-red-900/20 border-red-500/30'
                      }`}
                      title={svc.message || svc.error || 'OK'}
                    >
                      <div className={`text-[7px] font-mono font-bold uppercase ${getStatusColor(svc.status)}`}>
                        {getStatusIcon(svc.status)} {name.replace('_', ' ').slice(0, 8)}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Repair Suggestions */}
                {diagnostic.repair_suggestions?.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-orange-500/20">
                    <div className="text-[7px] text-orange-400 font-mono mb-0.5">Actions suggérées:</div>
                    {diagnostic.repair_suggestions.slice(0, 2).map((s, i) => (
                      <div key={i} className="text-[7px] text-gray-400 font-mono truncate">
                        • {s.service}: {s.action}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {!diagnostic && !diagLoading && (
              <div className="text-[8px] text-gray-500 font-mono text-center py-2">
                Cliquez SCANNER pour vérifier les connexions
              </div>
            )}
          </div>

          {/* Log des alertes */}
          <div className="flex-1 bg-black/70 border border-green-500/50 rounded p-2 flex flex-col overflow-hidden" style={{ boxShadow: '0 0 15px rgba(0, 255, 159, 0.1)' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] text-green-400 font-mono flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              HISTORIQUE ALERTES
            </div>
            <button
              onClick={clearLog}
              className="text-[8px] text-red-400 font-mono hover:text-red-300 flex items-center"
              title="Effacer l'historique"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
            {alertLog.length === 0 ? (
              <div className="text-[9px] text-gray-600 font-mono text-center py-4">
                Aucune alerte enregistrée
              </div>
            ) : (
              alertLog.map((entry) => (
                <div
                  key={entry.id}
                  className="bg-black/50 border-l-2 px-2 py-1 text-[8px] font-mono"
                  style={{ borderColor: entry.color }}
                >
                  <div className="flex justify-between items-center">
                    <span style={{ color: entry.color }} className="font-bold">{entry.type}</span>
                    <span className="text-gray-500">{entry.category}</span>
                  </div>
                  <div className="text-gray-400 mt-0.5">
                    {entry.date} {entry.time}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-2 pt-2 border-t border-green-500/30">
            <div className="text-[8px] text-gray-500 font-mono text-center">
              {alertLog.length} entrée{alertLog.length > 1 ? 's' : ''} • Max 50
            </div>
          </div>
        </div>
        </div>

      </div>
    </Layout>
  );
};

export default Admin;
